import { StanModal } from '@/components';
import { useUser } from '@/queries/useUser';
import { useSubscriptionPrices } from '@/hooks/useSubscriptionPrices';
import { type PaidSubscriptionTier, SUBSCRIPTION_INTERVALS, type SubscriptionInterval } from '@/lib/api/subscriptions';
import { formatCurrency } from '@/lib/currency';
import PayConfirmationSvg from '@/assets/images/pay_confirmation.svg';
import { BareSvg } from '@/lib';
import { format } from 'date-fns';

interface EstimateData {
    cost: string;
    date: string;
    status: string;
}

interface ChangePlanConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onGoBack: () => void;
    tier: PaidSubscriptionTier;
    interval: SubscriptionInterval;
    estimate: EstimateData | null;
    isUpdating: boolean;
}

export const ChangePlanConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    onGoBack,
    tier,
    interval,
    estimate,
    isUpdating,
}: ChangePlanConfirmationModalProps) => {
    const { user } = useUser();
    const { currency, getPrice } = useSubscriptionPrices();

    // Get the price for the selected tier and interval
    const price = getPrice(tier, interval);
    const priceTag = formatCurrency(price, currency);
    const stringInterval = interval === SUBSCRIPTION_INTERVALS.MONTH ? '/mo' : '/yr';

    // Compute start date text
    const today = format(new Date(), 'MMM d, yyyy');
    const computedStartDateOfNewPlan = estimate?.date
        ? today === estimate.date
            ? 'today.'
            : `${estimate.date}.`
        : 'today.';

    return (
        <StanModal
            id='confirm-change-plan-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Confirm Payment'
            className='w-full pt-4'
            primaryActionLabel='Yes, I understand'
            primaryActionLoading={isUpdating}
            primaryActionDisabled={isUpdating}
            onPrimaryAction={onConfirm}
            secondaryActionLabel='Go Back'
            secondaryActionDisabled={isUpdating}
            onSecondaryAction={onGoBack}
        >
            <div className='flex flex-col gap-7'>
                {/* Subtitle */}
                <p className='text-gray text-body-sm text-center font-medium'>
                    You will be charged the new fee starting {computedStartDateOfNewPlan}
                </p>
                {/* Header Card */}
                <div className='shadow-stan bg-canvas relative flex flex-col gap-7 rounded-md p-7'>
                    {/* Price Section */}
                    <div className='flex flex-col gap-1'>
                        <span className='text-gray text-body-xs'>
                            Your new rate starting {computedStartDateOfNewPlan}
                        </span>
                        <div className='flex items-end gap-1'>
                            <h1 className='text-semantic-fg-base text-title-display-2 leading-none font-bold'>
                                {priceTag}
                            </h1>
                            <h3 className='text-gray mb-1 text-2xl'>{stringInterval}</h3>
                        </div>
                    </div>

                    {/* Receipt Image */}
                    {/* {PaymentConfirmationSVG} */}
                    <BareSvg
                        SvgComponent={PayConfirmationSvg}
                        className='shadow-nucleus-elevation-low absolute top-7 right-7 w-14 rounded-sm object-contain max-md:hidden'
                    />

                    {/* To/From Info */}
                    <div className='text-body-sm flex gap-4'>
                        {/* Labels */}
                        <div className='max-md:text-label-xs-regular flex flex-col gap-4'>
                            <span className='text-gray'>To</span>
                            <span className='text-gray'>From</span>
                        </div>
                        {/* Values */}
                        <div className='max-md:text-label-xs-regular flex flex-col gap-4'>
                            <span className='text-dark font-bold'>@{user?.username}</span>
                            <span className='text-dark font-bold'>Stan - Your All-in-One Creator Store</span>
                        </div>
                    </div>

                    {/* Billing Info */}
                    <div className='bg-neutral-subtle flex justify-center rounded-md p-3'>
                        {estimate?.status === 'upgrading' ? (
                            <span className='text-gray text-body-xs'>
                                You will be charged a prorated amount of{' '}
                                <span className='font-bold'>{estimate.cost}</span> today if you accept
                            </span>
                        ) : (
                            <span className='text-gray text-body-xs'>
                                Your Stan Subscription is a taxable write-off for your business.
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </StanModal>
    );
};
